import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Table } from '../../components/ui/Table';
import { webhooksApi, type WebhookInput, type WebhookRecord } from '../../api/webhooks';
import '../operations.css';

const emptyForm: WebhookInput = { name: '', url: '', is_active: true, secret: '' };

export function WebhookList() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<WebhookRecord | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WebhookRecord | null>(null);
  const [form, setForm] = useState<WebhookInput>(emptyForm);
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => (await webhooksApi.getAll()).data,
  });

  const saveMutation = useMutation({
    mutationFn: async ({ id, input }: { id?: number; input: WebhookInput }) =>
      id ? webhooksApi.update(id, input) : webhooksApi.create(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setFormOpen(false);
      setForm(emptyForm);
      setEditing(null);
      setNotice(editing ? 'Webhook updated.' : 'Webhook created.');
      setActionError('');
    },
    onError: () => setActionError('Could not save webhook. Check the values and your access, then retry.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => webhooksApi.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setDeleteTarget(null);
      setNotice('Webhook deleted.');
      setActionError('');
    },
    onError: () => setActionError('Could not delete webhook. Check your access and retry.'),
  });

  const validateMutation = useMutation({
    mutationFn: (id: number) => webhooksApi.validate(id),
    onSuccess: () => { setNotice('Validation event sent successfully.'); setActionError(''); },
    onError: () => setActionError('Could not validate webhook. Check the destination and remote service, then retry.'),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setNotice('');
    setActionError('');
    setFormOpen(true);
  };

  const openEdit = (webhook: WebhookRecord) => {
    setEditing(webhook);
    setForm({ name: webhook.name, url: webhook.url, is_active: webhook.is_active, secret: '' });
    setNotice('');
    setActionError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saveMutation.isPending) return;
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setActionError('');
  };

  const columns = [
    { key: 'name', header: 'Name', render: (webhook: WebhookRecord) => <span className="font-medium">{webhook.name}</span> },
    { key: 'url', header: 'Destination', render: (webhook: WebhookRecord) => <span className="break-all">{webhook.url}</span> },
    { key: 'status', header: 'Status', render: (webhook: WebhookRecord) => webhook.is_active ? 'Enabled' : 'Disabled' },
    {
      key: 'actions', header: 'Actions', className: 'text-right',
      render: (webhook: WebhookRecord) => <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" aria-label={`Validate ${webhook.name}`} loading={validateMutation.isPending && validateMutation.variables === webhook.id} disabled={validateMutation.isPending} onClick={() => { setNotice(''); setActionError(''); validateMutation.mutate(webhook.id); }}><RefreshCw size={14} /> Validate</Button>
        <Button type="button" variant="secondary" size="sm" aria-label={`Edit ${webhook.name}`} onClick={() => openEdit(webhook)}><Pencil size={14} /> Edit</Button>
        <Button type="button" variant="danger" size="sm" aria-label={`Delete ${webhook.name}`} onClick={() => { setActionError(''); setDeleteTarget(webhook); }}><Trash2 size={14} /> Delete</Button>
      </div>,
    },
  ];

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setNotice('');
    setActionError('');
    saveMutation.mutate({ id: editing?.id, input: { ...form } });
  }

  return <div className="operations-page space-y-6">
    <div className="flex items-center justify-between gap-3">
      <div><p className="operations-eyebrow">Integrations</p><h1 className="text-2xl font-bold text-gray-900">Webhooks</h1><p className="text-gray-500 mt-1">Configure destinations for AwareNow event notifications.</p></div>
      <Button onClick={openCreate}><Plus size={16} /> New webhook</Button>
    </div>

    {notice && <p role="status" className="text-green-700">{notice}</p>}
    {actionError && <p role="alert" className="text-red-700">{actionError}</p>}
    {isError && <p role="alert" className="text-red-700">Could not load webhooks. Check your access and retry.</p>}

    <Card>
      {isLoading ? <p role="status">Loading webhooks…</p> :
        <Table columns={columns} data={data ?? []} keyExtractor={(webhook) => webhook.id} emptyMessage="No webhooks configured." />}
    </Card>

    <Modal isOpen={formOpen} onClose={closeForm} title={editing ? 'Edit webhook' : 'New webhook'} size="lg">
      <form onSubmit={submit} className="space-y-4">
        <label className="label" htmlFor="webhook-name">Name<input id="webhook-name" className="input" maxLength={120} required value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} /></label>
        <label className="label" htmlFor="webhook-url">Destination URL<input id="webhook-url" className="input" type="url" required value={form.url} onChange={event => setForm({ ...form, url: event.target.value })} placeholder="https://example.com/webhook" /></label>
        <label className="label" htmlFor="webhook-secret">Signing secret<input id="webhook-secret" className="input" type="password" autoComplete="new-password" value={form.secret ?? ''} onChange={event => setForm({ ...form, secret: event.target.value })} placeholder={editing ? 'Leave empty to keep the current secret' : 'Optional'} /></label>
        <p className="text-sm text-gray-600">Stored secrets are never displayed. {editing ? 'Leave this field empty to keep the current secret.' : 'Enter a secret if the receiving system requires webhook signing.'}</p>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={event => setForm({ ...form, is_active: event.target.checked })} />Enabled</label>
        {actionError && <p role="alert" className="text-red-700">{actionError}</p>}
        <div className="flex justify-end gap-3"><Button type="button" variant="secondary" onClick={closeForm}>Cancel</Button><Button type="submit" loading={saveMutation.isPending}>{editing ? 'Save webhook' : 'Create webhook'}</Button></div>
      </form>
    </Modal>

    <Modal isOpen={Boolean(deleteTarget)} onClose={() => !deleteMutation.isPending && setDeleteTarget(null)} title="Delete webhook" size="sm">
      <p>Delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.</p>
      {actionError && <p role="alert" className="text-red-700 mt-3">{actionError}</p>}
      <div className="flex justify-end gap-3 mt-4"><Button type="button" variant="secondary" disabled={deleteMutation.isPending} onClick={() => setDeleteTarget(null)}>Cancel</Button><Button type="button" variant="danger" loading={deleteMutation.isPending} onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}>Delete webhook</Button></div>
    </Modal>
  </div>;
}
